import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import VehicleCard from '../components/VehicleCard';

const { FiSearch, FiFilter, FiX } = FiIcons;

const CatalogPage = () => {
  const { vehicles, loading } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ brand: '', fuel: '', type: '' });

  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      if (vehicle.status !== 'active') return false;
      
      const matchesSearch = searchTerm === '' || 
        `${vehicle.brand} ${vehicle.model}`.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesBrand = !filters.brand || vehicle.brand === filters.brand;
      const matchesFuel = !filters.fuel || vehicle.fuel === filters.fuel;
      
      return matchesSearch && matchesBrand && matchesFuel;
    });
  }, [vehicles, searchTerm, filters]);

  const brands = [...new Set(vehicles.map(v => v.brand))];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#2c3e50] mb-4">Nuestro Catálogo</h1>
          <p className="text-xl text-gray-600">Encuentra el coche perfecto entre nuestra selección de calidad</p>
        </div>

        {/* Filters Bar */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              placeholder="Buscar marca o modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#005c91] outline-none"
            />
          </div>
          <select 
            value={filters.brand}
            onChange={(e) => setFilters(prev => ({...prev, brand: e.target.value}))}
            className="w-full px-4 py-2 border rounded-lg outline-none"
          >
            <option value="">Todas las marcas</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <button 
            onClick={() => {setSearchTerm(''); setFilters({brand:'', fuel:'', type:''})}}
            className="flex items-center justify-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
          >
            <SafeIcon icon={FiX} /> Limpiar filtros
          </button>
        </div>

        {/* Results Area */}
        {loading ? (
          // Loader while fetching
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#005c91] mb-4"></div>
            <p className="text-gray-500 text-lg font-medium">Cargando vehículos disponibles...</p>
          </div>
        ) : filteredVehicles.length > 0 ? (
          // Results
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredVehicles.map((vehicle) => (
                <motion.div
                  key={vehicle.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <VehicleCard vehicle={vehicle} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          // No results (only after loading is finished)
          <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-dashed border-gray-300">
            <SafeIcon icon={FiFilter} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">No se encontraron vehículos</h3>
            <p className="text-gray-500">Intenta cambiar los términos de búsqueda o filtros.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogPage;